// SPDX-License-Identifier: AGPL-3.0-or-later

import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Platform} from '@app/features/platform/types/Platform';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import {getElectronAPI, isDesktop, isNativeMacOS} from '@app/features/ui/utils/NativeUtils';
import {getStreamKey} from '@app/features/voice/components/StreamKeys';
import AdaptiveScreenShareEngine from '@app/features/voice/engine/AdaptiveScreenShareEngine';
import {
	getVoiceEngine,
	shouldUseNativeVoiceEngine,
} from '@app/features/voice/engine/native_voice_engine/getVoiceEngine';
import {
	markScreenShareCaptureActive,
	markScreenShareCaptureEnded,
} from '@app/features/voice/engine/ScreenShareCaptureDiagnostics';
import type {NegotiationReason} from '@app/features/voice/engine/ScreenShareCodecNegotiation';
import ScreenShareCodecNegotiation from '@app/features/voice/engine/ScreenShareCodecNegotiation';
import ScreenSharePublicationMigration from '@app/features/voice/engine/ScreenSharePublicationMigration';
import VoiceConnectionManager from '@app/features/voice/engine/VoiceConnectionManager';
import {updateLocalParticipantFromRoom} from '@app/features/voice/engine/VoiceMediaEngineBridge';
import VoiceMediaStateCoordinator from '@app/features/voice/engine/VoiceMediaStateCoordinator';
import {selectScreenShareCodecRepublishDecision} from '@app/features/voice/engine/VoiceScreenShareCodecRepublishPolicy';
import {
	createVoiceScreenShareSnapshot,
	type PendingScreenShareCodecRepublishRequest,
	transitionVoiceScreenShareSnapshot,
	type VoiceScreenShareEvent,
	type VoiceScreenShareSnapshot,
	type VoiceScreenShareSourceType,
} from '@app/features/voice/engine/VoiceScreenShareStateMachine';
import {addWatchedStreamKey, stopWatchingStreamKey} from '@app/features/voice/engine/VoiceStreamWatchState';
import {
	enforceLocalMediaPublicationCap,
	getLocalScreenSharePublications,
	getLocalScreenShareVideoPublications,
	unpublishLocalMediaPublications,
} from '@app/features/voice/engine/VoiceTrackPublicationUtils';
import {createDeviceReplacementTracks} from '@app/features/voice/engine/voice_screen_share_manager/DeviceMediaCapture';
import {
	createDisplayScreenShareTracks,
	createNativeScreenShareTracks,
	type NativeScreenShareOptions,
	startNativeCaptureForEngine,
	stopNativeCaptureForEngine,
} from '@app/features/voice/engine/voice_screen_share_manager/DisplayMediaCapture';
import {
	ensureNativeCameraPermissionForDeviceShare,
	ensureNativeMicrophonePermissionForDeviceShare,
} from '@app/features/voice/engine/voice_screen_share_manager/NativePermissionGate';
import {
	applyScreenShareContentHint as applyScreenShareMotionContentHint,
	type CapturedScreenShareTracks,
	captureScreenSharePublicationCleanup,
	type DeviceScreenShareCaptureOptions,
	enforceScreenShareSenderParameters as enforceScreenShareSenderParametersForSender,
	getPreferredScreenShareCodec,
	getReplacementScreenShareSettingsOptions,
	logger,
	mergeScreenShareCaptureCleanupSnapshots,
	type PendingScreenShareStopRequest,
	releaseScreenShareCaptureCleanup,
	getEffectivePublishOptions as resolveEffectivePublishOptions,
	type ScreenShareCaptureCleanupSnapshot,
	type ScreenShareCodecReadinessStatus,
	type SimulcastTrackInfoLike,
	scheduleScreenShareEncoderVerification,
	stopMediaTrack,
} from '@app/features/voice/engine/voice_screen_share_manager/shared';
import ActiveScreenShareSource from '@app/features/voice/state/ActiveScreenShareSource';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import MediaPermission from '@app/features/voice/state/MediaPermission';
import SoftwareEncoderWarning from '@app/features/voice/state/SoftwareEncoderWarning';
import VoiceSettings from '@app/features/voice/state/VoiceSettings';
import {
	prepareHighFidelityScreenShareAudioTrack,
	SCREEN_SHARE_AUDIO_PUBLISH_OPTIONS,
} from '@app/features/voice/utils/AudioPublishOptions';
import {
	markScreenShareCodecEncodeRuntimeFailure,
	resolveScreenShareContentHintForContext,
	type ScreenShareContentSource,
} from '@app/features/voice/utils/CodecCapabilityDetector';
import {disarmVirtmic} from '@app/features/voice/utils/LinuxScreenShareAudio';
import {
	captureNativeAudioTrackForLinuxRouting,
	commitNativeAudioBridgeReplacement,
	disarmNativeAudio,
} from '@app/features/voice/utils/NativeAudioCaptureBridge';
import {ensureNativePermission} from '@app/features/voice/utils/NativePermissions';
import {ScreenRecordingPermissionDeniedError} from '@app/features/voice/utils/ScreenRecordingPermissionDeniedError';
import {SCREEN_SHARE_DEGRADATION_PREFERENCE} from '@app/features/voice/utils/ScreenShareOptions';
import {applyCameraMirrorProcessor} from '@app/features/voice/utils/VideoBackgroundProcessor';
import type {NativeAudioStartOptions, NativeScreenCaptureSource} from '@app/types/electron.d';
import {
	createLocalAudioTrack,
	createLocalVideoTrack,
	type LocalAudioTrack,
	type LocalParticipant,
	type LocalTrackPublication,
	type LocalVideoTrack,
	type Room,
	type ScreenShareCaptureOptions,
	Track,
	type TrackPublishOptions,
	type VideoCodec,
} from 'livekit-client';
import {makeAutoObservable} from 'mobx';

export type {DeviceScreenShareCaptureOptions} from '@app/features/voice/engine/voice_screen_share_manager/shared';

export interface ScreenShareReconnectSnapshot {
	videoTrack: MediaStreamTrack;
	audioTrack?: MediaStreamTrack;
	audioMuted: boolean;
	contentSource: ScreenShareContentSource;
}

function isVideoCodecValue(value: unknown): value is VideoCodec {
	return value === 'av1' || value === 'h265' || value === 'h264' || value === 'vp9' || value === 'vp8';
}

class VoiceScreenShareManager {
	private screenShareLifecycle: VoiceScreenShareSnapshot = createVoiceScreenShareSnapshot();
	isScreenSharePending = false;
	private activeScreenShareEndListener: (() => void) | null = null;
	private endedScreenShareStopInFlight: Promise<void> | null = null;
	private nativeEngineScreenShareCaptureId: string | null = null;
	private encoderVerificationTimer: NodeJS.Timeout | null = null;
	private codecRepublishInFlight = false;
	private screenShareMigrationGeneration = 0;
	private screenShareKeepAliveElement: HTMLVideoElement | null = null;
	private screenShareKeepAliveTrack: LocalVideoTrack | null = null;

	constructor() {
		makeAutoObservable(
			this,
			{
				screenShareKeepAliveElement: false,
				screenShareKeepAliveTrack: false,
			} as never,
			{autoBind: true},
		);
	}

	getIsScreenSharePending(): boolean {
		return this.isScreenSharePending;
	}

	private streamingPriorityHeld = false;

	private setStreamingPriority(active: boolean): void {
		if (active === this.streamingPriorityHeld) return;
		this.transitionScreenShareLifecycle({type: 'share.streamingPriority.set', active});
		try {
			const api = getElectronAPI();
			if (active) {
				api?.acquireStreamingPriority?.();
			} else {
				api?.releaseStreamingPriority?.();
			}
		} catch {}
	}

	private getOrCreateScreenShareKeepAliveElement(): HTMLVideoElement | null {
		if (typeof document === 'undefined' || !document.body) return null;
		if (this.screenShareKeepAliveElement?.isConnected) return this.screenShareKeepAliveElement;
		const element = document.createElement('video');
		element.autoplay = true;
		element.muted = true;
		element.playsInline = true;
		element.setAttribute('aria-hidden', 'true');
		element.setAttribute('data-flx', 'voice.screen-share-keepalive');
		Object.assign(element.style, {
			position: 'fixed',
			left: '0',
			top: '0',
			width: '2px',
			height: '2px',
			opacity: '0.001',
			pointerEvents: 'none',
			zIndex: '0',
		});
		document.body.appendChild(element);
		this.screenShareKeepAliveElement = element;
		return element;
	}

	private ensureScreenShareKeepAliveSink(participant: LocalParticipant, preferredTrack?: LocalVideoTrack): void {
		const screenShareTrack =
			preferredTrack ??
			(participant.getTrackPublication(Track.Source.ScreenShare)?.videoTrack as LocalVideoTrack | undefined);
		if (!screenShareTrack || screenShareTrack.mediaStreamTrack.readyState === 'ended') {
			this.clearScreenShareKeepAliveSink();
			return;
		}
		const element = this.getOrCreateScreenShareKeepAliveElement();
		if (!element) return;
		if (this.screenShareKeepAliveTrack && this.screenShareKeepAliveTrack !== screenShareTrack) {
			try {
				this.screenShareKeepAliveTrack.detach(element);
			} catch (error) {
				logger.debug('Failed to detach stale screen-share keepalive sink', {error});
			}
		}
		this.screenShareKeepAliveTrack = screenShareTrack;
		try {
			screenShareTrack.attach(element);
			void element.play().catch(() => {});
		} catch (error) {
			logger.warn('Failed to attach screen-share keepalive sink', {error});
		}
	}

	private clearScreenShareKeepAliveSink(): void {
		const element = this.screenShareKeepAliveElement;
		const track = this.screenShareKeepAliveTrack;
		this.screenShareKeepAliveElement = null;
		this.screenShareKeepAliveTrack = null;
		if (track && element) {
			try {
				track.detach(element);
			} catch (error) {
				logger.debug('Failed to detach screen-share keepalive sink', {error});
			}
		}
		if (element) {
			element.pause();
			element.srcObject = null;
			element.remove();
		}
	}

	isScreenShareCodecRepublishInFlight(): boolean {
		return this.codecRepublishInFlight;
	}

	private transitionScreenShareLifecycle(event: VoiceScreenShareEvent): void {
		this.screenShareLifecycle = transitionVoiceScreenShareSnapshot(this.screenShareLifecycle, event);
		this.isScreenSharePending = this.screenShareLifecycle.context.pendingOperation != null;
		this.codecRepublishInFlight = this.screenShareLifecycle.context.codecRepublishInFlight;
		this.streamingPriorityHeld = this.screenShareLifecycle.context.streamingPriorityHeld;
	}

	private transitionScreenShareCodecReadiness(status: ScreenShareCodecReadinessStatus): void {
		this.transitionScreenShareLifecycle({
			type:
				status === 'loading'
					? 'share.codecReadiness.loading'
					: status === 'ready'
						? 'share.codecReadiness.ready'
						: 'share.codecReadiness.timeout',
		});
	}

	private async getEffectivePublishOptions(
		enabled: boolean,
		publishOptions?: TrackPublishOptions,
	): Promise<TrackPublishOptions | undefined> {
		return resolveEffectivePublishOptions(enabled, publishOptions, {
			onCodecReadiness: (status) => this.transitionScreenShareCodecReadiness(status),
		});
	}

	private queuePendingStopRequest(options?: {sendUpdate?: boolean; playSound?: boolean}): void {
		this.transitionScreenShareLifecycle({
			type: 'share.stop',
			request: {
				sendUpdate: options?.sendUpdate ?? true,
				playSound: options?.playSound ?? true,
			},
		});
	}

	private takePendingStopRequest(): PendingScreenShareStopRequest | null {
		const request = this.screenShareLifecycle.context.queuedStopRequest;
		this.transitionScreenShareLifecycle({type: 'share.queuedStop.clear'});
		return request;
	}

	private async applyPendingStopRequest(room: Room | null, participant: LocalParticipant): Promise<void> {
		const request = this.takePendingStopRequest();
		if (!request || !participant.isScreenShareEnabled) {
			return;
		}
		await this.setScreenShareEnabled(room, false, request);
	}

	private queuePendingCodecRepublishRequest(
		codec: VideoCodec,
		reason: NegotiationReason,
		options: {force?: boolean} = {},
	): void {
		this.transitionScreenShareLifecycle({
			type: 'share.codecRepublish.queue',
			request: {
				codec,
				reason,
				force: options.force === true,
			},
		});
	}

	private deferActiveCodecRepublishRequest(
		codec: VideoCodec,
		reason: NegotiationReason,
		options: {force?: boolean} = {},
	): void {
		this.transitionScreenShareLifecycle({
			type: 'share.codecRepublish.defer',
			request: {
				codec,
				reason,
				force: options.force === true,
			},
		});
	}

	private takePendingCodecRepublishRequest(): PendingScreenShareCodecRepublishRequest | null {
		const request = this.screenShareLifecycle.context.queuedCodecRepublishRequest;
		this.transitionScreenShareLifecycle({type: 'share.queuedCodecRepublish.clear'});
		return request;
	}

	private async applyPendingCodecRepublishRequest(room: Room | null, participant: LocalParticipant): Promise<void> {
		const request = this.takePendingCodecRepublishRequest();
		if (!request || !participant.isScreenShareEnabled) {
			return;
		}
		await this.renegotiateActiveScreenShareCodec(room, request.codec, request.reason, {force: request.force});
	}

	private async applyPendingScreenShareRequests(room: Room | null, participant: LocalParticipant): Promise<void> {
		await this.applyPendingStopRequest(room, participant);
		await this.applyPendingCodecRepublishRequest(room, participant);
	}

	private getActiveScreenShareContentSource(): ScreenShareContentSource {
		const sourceId = ActiveScreenShareSource.getSourceId();
		if (sourceId?.startsWith('window:')) return 'app';
		return 'display';
	}

	private getActiveScreenShareSourceType(): VoiceScreenShareSourceType {
		const contentSource = this.getActiveScreenShareContentSource();
		if (contentSource === 'device') return 'device';
		return 'display';
	}

	private getNativeScreenShareSourceType(nativeOptions: NativeScreenShareOptions): VoiceScreenShareSourceType {
		return nativeOptions.source.kind === 'window' ? 'native-app' : 'native-display';
	}

	private getScreenShareSourceTypeForContentSource(
		contentSource: ScreenShareContentSource,
	): VoiceScreenShareSourceType {
		if (contentSource === 'device') return 'device';
		return 'display';
	}

	private applyScreenShareContentHint(
		participant: LocalParticipant,
		contentSource: ScreenShareContentSource = this.getActiveScreenShareContentSource(),
		preferredTrack?: LocalVideoTrack,
	): void {
		const publication = preferredTrack ? undefined : participant.getTrackPublication(Track.Source.ScreenShare);
		const track = preferredTrack ?? publication?.videoTrack;
		if (!track) {
			return;
		}
		this.applyScreenShareContentHintToMediaTrack(track.mediaStreamTrack, contentSource);
	}

	private applyScreenShareContentHintToMediaTrack(
		mediaStreamTrack: MediaStreamTrack | undefined,
		contentSource: ScreenShareContentSource = this.getActiveScreenShareContentSource(),
	): void {
		if (!mediaStreamTrack) return;
		const hint = resolveScreenShareContentHintForContext(
			VoiceSettings.getScreenShareContentHintOverride(),
			getPreferredScreenShareCodec(),
			contentSource,
			VoiceSettings.getStreamingMode(),
		);
		if (!hint) {
			mediaStreamTrack.contentHint = '';
			return;
		}
		applyScreenShareMotionContentHint(mediaStreamTrack, hint);
	}

	private async enforceScreenShareSenderParameters(
		participant: LocalParticipant,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		const publication = participant.getTrackPublication(Track.Source.ScreenShare);
		const track = publication?.videoTrack as LocalVideoTrack | undefined;
		await this.enforceScreenShareTrackSenderParameters(track, publishOptions);
	}

	private async enforceScreenShareTrackSenderParameters(
		track: LocalVideoTrack | undefined,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		let applied = await enforceScreenShareSenderParametersForSender(track?.sender, publishOptions);
		const simulcastCodecs = (
			track as
				| (LocalVideoTrack & {
						simulcastCodecs?: Map<unknown, SimulcastTrackInfoLike>;
				  })
				| undefined
		)?.simulcastCodecs;
		if (simulcastCodecs?.size) {
			for (const [codec, simulcastTrackInfo] of simulcastCodecs) {
				const codecOverride = isVideoCodecValue(codec) ? codec : undefined;
				const backupApplied = await enforceScreenShareSenderParametersForSender(
					simulcastTrackInfo.sender,
					publishOptions,
					codecOverride,
				);
				applied = applied || backupApplied;
			}
		}
		if (!applied) {
			logger.warn('No sender found for screen share sender parameter enforcement');
		}
	}

	private applyScreenShareAudioContentHint(participant: LocalParticipant): void {
		const publication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const track =
			publication?.audioTrack?.mediaStreamTrack ??
			(publication?.track as LocalAudioTrack | undefined)?.mediaStreamTrack;
		prepareHighFidelityScreenShareAudioTrack(track);
	}

	private getLocalStreamKey(): string | null {
		const {guildId, channelId, connectionId} = VoiceConnectionManager.connectionState;
		if (!connectionId) {
			logger.debug('Skipping local stream watcher sync without an active voice connection', {
				connectionState: VoiceConnectionManager.connectionState,
			});
			return null;
		}
		return getStreamKey(guildId, channelId, connectionId);
	}

	prepareScreenShareReconnect(room: Room | null): ScreenShareReconnectSnapshot | null {
		const participant = room?.localParticipant;
		if (!participant?.isScreenShareEnabled) {
			return null;
		}
		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const videoTrack = screenSharePublication?.videoTrack?.mediaStreamTrack;
		if (!videoTrack || videoTrack.readyState === 'ended') {
			return null;
		}
		const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const audioTrack =
			screenShareAudioPublication?.audioTrack?.mediaStreamTrack ??
			(screenShareAudioPublication?.track as LocalAudioTrack | undefined)?.mediaStreamTrack;
		const liveAudioTrack = audioTrack && audioTrack.readyState !== 'ended' ? audioTrack : undefined;
		return {
			videoTrack,
			...(liveAudioTrack ? {audioTrack: liveAudioTrack} : {}),
			audioMuted: screenShareAudioPublication?.isMuted ?? false,
			contentSource: this.getActiveScreenShareContentSource(),
		};
	}

	private syncLocalScreenShareAudioState(participant: LocalParticipant, enabled: boolean): void {
		const hasAudioTrack = Boolean(participant.getTrackPublication(Track.Source.ScreenShareAudio));
		LocalVoiceState.updateSelfStreamAudio(enabled && hasAudioTrack);
	}

	private syncPersistedScreenShareAudioPreference(participant: LocalParticipant): void {
		const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const muteStreamAudio = !(screenShareAudioPublication && !screenShareAudioPublication.isMuted);
		if (VoiceSettings.getMuteStreamAudio() !== muteStreamAudio) {
			VoiceSettings.updateSettings({muteStreamAudio});
		}
	}

	private async unmuteScreenShareAudioPublication(participant: LocalParticipant, reason: string): Promise<void> {
		const publication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		if (!publication || !publication.isMuted) {
			return;
		}
		try {
			await publication.unmute();
		} catch (error) {
			logger.warn('Failed to unmute screen-share audio publication', {error, reason});
		}
	}

	private cleanupScreenShareAudioRoutingState(): void {
		this.cleanupScreenShareAudioCaptureRouting();
		ActiveScreenShareSource.clear();
	}

	private cleanupScreenShareAudioCaptureRouting(): void {
		void getElectronAPI()?.virtmic?.stop();
		disarmVirtmic();
		disarmNativeAudio();
	}

	private getScreenShareCaptureCleanupSnapshot(participant: LocalParticipant): ScreenShareCaptureCleanupSnapshot {
		return captureScreenSharePublicationCleanup(...getLocalScreenSharePublications(participant));
	}

	private async releaseScreenShareCapture(
		participant: LocalParticipant,
		snapshot?: ScreenShareCaptureCleanupSnapshot,
	): Promise<void> {
		await releaseScreenShareCaptureCleanup(
			mergeScreenShareCaptureCleanupSnapshots(snapshot, this.getScreenShareCaptureCleanupSnapshot(participant)),
		);
	}

	private getScreenShareSimulcastCleanupSnapshot(screenShareTrack: LocalVideoTrack): ScreenShareCaptureCleanupSnapshot {
		const snapshot: ScreenShareCaptureCleanupSnapshot = {mediaTracks: [], senders: []};
		const simulcastCodecs = (
			screenShareTrack as LocalVideoTrack & {
				simulcastCodecs?: Map<unknown, SimulcastTrackInfoLike>;
			}
		).simulcastCodecs;
		for (const simulcastTrackInfo of simulcastCodecs?.values() ?? []) {
			snapshot.mediaTracks.push(simulcastTrackInfo.mediaStreamTrack);
			if (simulcastTrackInfo.sender) {
				snapshot.senders.push(simulcastTrackInfo.sender);
			}
		}
		return snapshot;
	}

	private cleanupActiveScreenShareEndListener(): void {
		this.activeScreenShareEndListener?.();
		this.activeScreenShareEndListener = null;
	}

	private cancelEncoderVerification(): void {
		if (this.encoderVerificationTimer != null) {
			clearTimeout(this.encoderVerificationTimer);
			this.encoderVerificationTimer = null;
		}
		this.transitionScreenShareLifecycle({type: 'share.encoderVerification.cleared'});
	}

	private startEncoderVerification(
		room: Room | null,
		participant: LocalParticipant,
		codec?: VideoCodec,
		preferredTrack?: LocalVideoTrack,
	): void {
		this.cancelEncoderVerification();
		if (!codec) return;
		const publication = preferredTrack ? undefined : participant.getTrackPublication(Track.Source.ScreenShare);
		const track = preferredTrack ?? (publication?.videoTrack as LocalVideoTrack | undefined);
		const sender = track?.sender;
		if (!sender) {
			logger.warn('No sender found for screen share encoder verification');
			return;
		}
		this.encoderVerificationTimer = scheduleScreenShareEncoderVerification(
			() => sender.getStats(),
			codec,
			(failure) => {
				const failureReason =
					failure.reason === 'codec-mismatch' ? 'screen-share-codec-mismatch' : 'screen-share-encode-stalled';
				if (!markScreenShareCodecEncodeRuntimeFailure(failure.codec, failureReason)) return;
				void this.recoverActiveScreenShareAfterEncoderFailure(room, failure.codec).catch((error) => {
					logger.warn('Failed to recover screen share after encoder verification failure', {
						error,
						codec: failure.codec,
						failureReason,
					});
				});
			},
		);
		this.transitionScreenShareLifecycle({type: 'share.encoderVerification.scheduled'});
	}

	private async recoverActiveScreenShareAfterEncoderFailure(room: Room | null, failedCodec: VideoCodec): Promise<void> {
		await ScreenShareCodecNegotiation.publishLocalCapabilities(room, 'manual', {});
		const codec = ScreenShareCodecNegotiation.selectScreenShareCodec(VoiceSettings.getPreferredScreenShareCodec());
		if (codec === failedCodec) return;
		await this.renegotiateActiveScreenShareCodec(room, codec, 'manual', {force: true});
	}

	private monitorActiveScreenShareEnd(
		room: Room | null,
		participant: LocalParticipant,
		preferredTrack?: LocalVideoTrack,
	): void {
		this.cleanupActiveScreenShareEndListener();
		const publication = preferredTrack ? undefined : participant.getTrackPublication(Track.Source.ScreenShare);
		const videoTrack = preferredTrack ?? publication?.videoTrack;
		const mediaStreamTrack = videoTrack?.mediaStreamTrack;
		if (!mediaStreamTrack) {
			return;
		}
		const isCurrentScreenShareTrack = (): boolean =>
			getLocalScreenShareVideoPublications(participant).some((screenSharePublication) => {
				const track =
					(screenSharePublication.videoTrack as LocalVideoTrack | undefined) ??
					(screenSharePublication.track as LocalVideoTrack | undefined);
				return track === videoTrack && track.mediaStreamTrack === mediaStreamTrack;
			});
		const stopEndedScreenShare = (trigger: string): void => {
			if (this.endedScreenShareStopInFlight) return;
			if (!isCurrentScreenShareTrack()) {
				return;
			}
			logger.info('Screen share media track ended; disabling screen share', {
				trigger,
				readyState: mediaStreamTrack.readyState,
			});
			this.transitionScreenShareLifecycle({type: 'share.endedStop.start'});
			const stopPromise = this.setScreenShareEnabled(room, false, {sendUpdate: true, playSound: true})
				.catch((error) => {
					logger.warn('Failed to disable screen share after media track ended', {error});
				})
				.finally(() => {
					if (this.endedScreenShareStopInFlight === stopPromise) {
						this.endedScreenShareStopInFlight = null;
					}
					this.transitionScreenShareLifecycle({type: 'share.endedStop.finish'});
				});
			this.endedScreenShareStopInFlight = stopPromise;
		};
		const onEnded = (): void => stopEndedScreenShare('ended-event');
		mediaStreamTrack.addEventListener('ended', onEnded);
		this.activeScreenShareEndListener = () => {
			mediaStreamTrack.removeEventListener('ended', onEnded);
		};
		if (mediaStreamTrack.readyState === 'ended') {
			queueMicrotask(() => stopEndedScreenShare('already-ended'));
		}
	}

	async restoreScreenShareReconnect(
		room: Room | null,
		snapshot: ScreenShareReconnectSnapshot,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant) {
			logger.warn('No participant');
			return false;
		}
		if (participant.isScreenShareEnabled) {
			await enforceLocalMediaPublicationCap(participant, 'screen_share');
			this.ensureScreenShareKeepAliveSink(participant);
			VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'server', sendUpdate: false});
			updateLocalParticipantFromRoom(room);
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: true,
				sourceType: this.getActiveScreenShareSourceType(),
				encoderVerificationScheduled: this.encoderVerificationTimer != null,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			return true;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring screen share reconnect restore');
			return false;
		}
		if (snapshot.videoTrack.readyState === 'ended') {
			logger.warn('Cannot restore screen share reconnect from ended video track');
			return false;
		}
		this.transitionScreenShareLifecycle({
			type: 'share.restore',
			sourceType: this.getScreenShareSourceTypeForContentSource(snapshot.contentSource),
		});
		let videoPublished = false;
		let audioPublished = false;
		try {
			if (getLocalScreenSharePublications(participant).length > 0) {
				await this.cleanupLingeringScreenShareTracks(participant);
			}
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, publishOptions);
			await participant.publishTrack(snapshot.videoTrack, {
				...effectivePublishOptions,
				source: Track.Source.ScreenShare,
				stream: 'screen_share',
			});
			videoPublished = true;
			const audioTrack = snapshot.audioTrack;
			if (audioTrack && audioTrack.readyState !== 'ended') {
				try {
					prepareHighFidelityScreenShareAudioTrack(audioTrack);
					await participant.publishTrack(audioTrack, SCREEN_SHARE_AUDIO_PUBLISH_OPTIONS);
					audioPublished = true;
					const audioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
					if (snapshot.audioMuted) {
						await audioPublication?.mute();
					} else {
						await this.unmuteScreenShareAudioPublication(participant, 'restore screen-share audio after reconnect');
					}
				} catch (error) {
					logger.warn('Failed to restore screen-share audio after reconnect; continuing video-only', {error});
				}
			}
			this.setStreamingPriority(true);
			await enforceLocalMediaPublicationCap(participant, 'screen_share');
			VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'server', sendUpdate: false});
			this.applyScreenShareContentHint(participant, snapshot.contentSource);
			await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
			this.ensureScreenShareKeepAliveSink(participant);
			this.applyScreenShareAudioContentHint(participant);
			this.monitorActiveScreenShareEnd(room, participant);
			this.startEncoderVerification(room, participant, effectivePublishOptions?.videoCodec);
			AdaptiveScreenShareEngine.start(room);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(true);
			this.syncLocalScreenShareAudioState(participant, true);
			this.syncPersistedScreenShareAudioPreference(participant);
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: true,
				sourceType: this.getScreenShareSourceTypeForContentSource(snapshot.contentSource),
				encoderVerificationScheduled: this.encoderVerificationTimer != null,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			logger.info('Restored screen share after voice reconnect', {audioPublished});
			return true;
		} catch (error) {
			logger.warn('Failed to restore screen share after voice reconnect', {error, videoPublished});
			const actual = participant.isScreenShareEnabled;
			VoiceMediaStateCoordinator.applyScreenShareState(actual, {reason: 'server', sendUpdate: false});
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);
			this.syncLocalScreenShareAudioState(participant, actual);
			if (!actual) {
				stopMediaTrack(snapshot.videoTrack);
				stopMediaTrack(snapshot.audioTrack);
			} else {
				AdaptiveScreenShareEngine.start(room);
			}
			this.transitionScreenShareLifecycle({
				type: 'share.reject',
				active: actual,
				sourceType: actual ? this.getScreenShareSourceTypeForContentSource(snapshot.contentSource) : null,
			});
			return actual;
		}
	}

	async cleanupLingeringScreenShareTracks(
		participant: LocalParticipant,
		snapshot?: ScreenShareCaptureCleanupSnapshot,
	): Promise<void> {
		this.clearScreenShareKeepAliveSink();
		this.cleanupScreenShareAudioRoutingState();
		const publications = getLocalScreenSharePublications(participant);
		const cleanupSnapshot = mergeScreenShareCaptureCleanupSnapshots(
			snapshot,
			captureScreenSharePublicationCleanup(...publications),
		);
		const cleanupResult = await unpublishLocalMediaPublications(participant, publications);
		for (const failure of cleanupResult.failedPublications) {
			logger.warn('Failed to unpublish lingering screen share track', {
				error: failure.error,
				source: failure.publication.source,
			});
		}
		await this.releaseScreenShareCapture(participant, cleanupSnapshot);
	}

	handleLocalScreenShareTrackUnpublished(room: Room, playSound: boolean, publication?: LocalTrackPublication): void {
		this.clearScreenShareKeepAliveSink();
		this.cleanupActiveScreenShareEndListener();
		AdaptiveScreenShareEngine.stop();
		const participant = room.localParticipant;
		const cleanupSnapshot = captureScreenSharePublicationCleanup(
			publication,
			participant.getTrackPublication(Track.Source.ScreenShareAudio),
		);
		this.syncLocalStreamWatchState(false);
		this.syncLocalScreenShareAudioState(participant, false);
		void this.cleanupLingeringScreenShareTracks(participant, cleanupSnapshot).catch((error) => {
			logger.warn('Failed to clean up screen-share audio after video unpublish', {error});
		});
		if (playSound && !this.isScreenSharePending) {
			SoundCommands.playSound(SoundType.ScreenShareStop);
		}
	}

	private syncLocalStreamWatchState(enabled: boolean): void {
		const streamKey = this.getLocalStreamKey();
		if (!streamKey) {
			return;
		}
		const current = LocalVoiceState.getViewerStreamKeys();
		this.transitionScreenShareLifecycle({
			type: 'share.localWatcher.sync',
			enabled,
			streamKey,
			currentViewerStreamKeys: current,
		});
		for (const command of this.screenShareLifecycle.context.watchCommands) {
			if (command.type === 'watch.add') {
				addWatchedStreamKey(command.key);
			} else {
				stopWatchingStreamKey(command.key, {clearPinned: false});
			}
		}
		this.transitionScreenShareLifecycle({type: 'share.clearWatchCommands'});
		if (!enabled && !current.includes(streamKey)) {
			logger.debug('Local stream watcher already absent while disabling screen share', {
				current,
				expected: streamKey,
			});
		}
	}

	private async replaceActiveScreenShareAudioTrack(
		participant: LocalParticipant,
		audioTrack: MediaStreamTrack | undefined,
	): Promise<boolean> {
		const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		const existingAudioTrack =
			screenShareAudioPublication?.audioTrack ?? (screenShareAudioPublication?.track as LocalAudioTrack | undefined);
		const previousAudioMediaTrack = existingAudioTrack?.mediaStreamTrack;
		if (audioTrack) {
			prepareHighFidelityScreenShareAudioTrack(audioTrack);
			if (existingAudioTrack) {
				await existingAudioTrack.replaceTrack(audioTrack, false);
				if (previousAudioMediaTrack && previousAudioMediaTrack !== existingAudioTrack.mediaStreamTrack) {
					stopMediaTrack(previousAudioMediaTrack);
				}
				await this.unmuteScreenShareAudioPublication(participant, 'replace screen-share audio track');
				return true;
			}
			await participant.publishTrack(audioTrack, SCREEN_SHARE_AUDIO_PUBLISH_OPTIONS);
			await this.unmuteScreenShareAudioPublication(participant, 'publish screen-share audio track');
			return true;
		}
		this.cleanupScreenShareAudioCaptureRouting();
		if (existingAudioTrack) {
			await participant.unpublishTrack(existingAudioTrack);
			stopMediaTrack(previousAudioMediaTrack);
		}
		return false;
	}

	async ensureLinuxScreenShareAudioPublication(
		room: Room | null,
		linuxRule?: NonNullable<NativeAudioStartOptions['linuxRule']>,
		options: {includeSelfWindowAudio?: boolean; replaceExisting?: boolean} = {},
	): Promise<boolean> {
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) return false;
		if (!linuxRule) return false;
		const capturedTrack = await captureNativeAudioTrackForLinuxRouting(linuxRule, options);
		if (!capturedTrack) return false;
		let adopted = false;
		try {
			adopted = await this.replaceActiveScreenShareAudioTrack(participant, capturedTrack);
			if (adopted) {
				commitNativeAudioBridgeReplacement();
			}
		} catch (error) {
			logger.warn('Failed to publish mid-stream Linux native screen-share audio track', {error});
			if (!adopted) {
				try {
					capturedTrack.stop();
				} catch {}
			}
			return false;
		}
		this.syncLocalScreenShareAudioState(participant, true);
		this.syncPersistedScreenShareAudioPreference(participant);
		return true;
	}

	private async refreshScreenShareSimulcastTracks(screenShareTrack: LocalVideoTrack): Promise<void> {
		const simulcastCodecs = (
			screenShareTrack as LocalVideoTrack & {
				simulcastCodecs?: Map<unknown, SimulcastTrackInfoLike>;
			}
		).simulcastCodecs;
		if (!simulcastCodecs?.size) {
			return;
		}
		for (const simulcastTrackInfo of simulcastCodecs.values()) {
			const previousTrack = simulcastTrackInfo.mediaStreamTrack;
			let nextTrack: MediaStreamTrack | undefined;
			try {
				nextTrack = screenShareTrack.mediaStreamTrack.clone();
				await simulcastTrackInfo.sender?.replaceTrack(nextTrack);
				simulcastTrackInfo.mediaStreamTrack = nextTrack;
			} catch (error) {
				stopMediaTrack(nextTrack);
				try {
					await simulcastTrackInfo.sender?.replaceTrack(null);
				} catch (replaceError) {
					logger.warn('Failed to stop stale screen share simulcast track after replacement failed', {
						error: replaceError,
					});
				}
				logger.warn('Failed to replace active screen share simulcast track', {error});
			} finally {
				stopMediaTrack(previousTrack);
			}
		}
	}

	private async replaceActiveScreenShareTracks(
		room: Room,
		participant: LocalParticipant,
		tracks: CapturedScreenShareTracks,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
		contentSource?: ScreenShareContentSource,
	): Promise<boolean> {
		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = screenSharePublication?.videoTrack;
		if (!screenShareTrack) {
			stopMediaTrack(tracks.videoTrack);
			stopMediaTrack(tracks.audioTrack);
			logger.warn('No active screen share video track to replace');
			return false;
		}
		const nextContentSource = contentSource ?? this.getActiveScreenShareContentSource();
		if (nextContentSource !== 'device' && screenShareTrack.getProcessor()) {
			await screenShareTrack.stopProcessor(false);
		}
		const previousVideoMediaTrack = screenShareTrack.mediaStreamTrack;
		try {
			await screenShareTrack.replaceTrack(tracks.videoTrack, false);
			if (nextContentSource === 'device') {
				await applyCameraMirrorProcessor(screenShareTrack);
			}
			await this.refreshScreenShareSimulcastTracks(screenShareTrack);
		} catch (error) {
			stopMediaTrack(tracks.videoTrack);
			stopMediaTrack(tracks.audioTrack);
			logger.error('Failed to replace active screen share video track', {error});
			return false;
		}
		if (previousVideoMediaTrack && previousVideoMediaTrack !== screenShareTrack.mediaStreamTrack) {
			this.cleanupActiveScreenShareEndListener();
			stopMediaTrack(previousVideoMediaTrack);
		}
		let audioTrackAdopted = false;
		try {
			audioTrackAdopted = await this.replaceActiveScreenShareAudioTrack(participant, tracks.audioTrack);
			if (audioTrackAdopted && tracks.audioTrack) {
				commitNativeAudioBridgeReplacement();
			}
		} catch (error) {
			if (!audioTrackAdopted) {
				stopMediaTrack(tracks.audioTrack);
			}
			logger.warn('Failed to replace active screen share audio track', {error});
		}
		await enforceLocalMediaPublicationCap(participant, 'screen_share');
		const effectivePublishOptions = await this.getEffectivePublishOptions(true, publishOptions);
		await this.updateActiveScreenShareSettings(
			room,
			getReplacementScreenShareSettingsOptions(options, tracks.audioTrack != null),
			effectivePublishOptions,
		);
		this.applyScreenShareContentHint(participant, nextContentSource);
		await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
		this.ensureScreenShareKeepAliveSink(participant);
		this.applyScreenShareAudioContentHint(participant);
		this.monitorActiveScreenShareEnd(room, participant);
		this.startEncoderVerification(room, participant, effectivePublishOptions?.videoCodec);
		AdaptiveScreenShareEngine.start(room);
		VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'user', sendUpdate: true});
		updateLocalParticipantFromRoom(room);
		this.syncLocalStreamWatchState(true);
		this.syncLocalScreenShareAudioState(participant, true);
		this.syncPersistedScreenShareAudioPreference(participant);
		logger.info('Replaced active screen share source', {
			audioIncluded: tracks.audioTrack != null,
		});
		return true;
	}

	async setScreenShareEnabled(
		room: Room | null,
		enabled: boolean,
		options?: ScreenShareCaptureOptions & {
			sendUpdate?: boolean;
			playSound?: boolean;
			restartIfEnabled?: boolean;
		},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return;
		}
		const {sendUpdate = true, playSound = true, restartIfEnabled = false, ...restOptions} = options || {};
		if (await shouldUseNativeVoiceEngine()) {
			if (!enabled) {
				await this.stopNativeEngineScreenShare({sendUpdate, playSound});
				return;
			}
			const nativeOptions = this.resolveNativeEngineScreenShareOptionsFromActiveSource(restOptions);
			if (!nativeOptions) {
				logger.warn('Native voice engine: no desktop source selected for screen-share enable; ignoring');
				return;
			}
			await this.startNativeEngineScreenShare(nativeOptions, {sendUpdate, playSound}, publishOptions);
			return;
		}
		const participant = room?.localParticipant;
		if (!participant) {
			logger.warn('No participant');
			return;
		}
		if (this.isScreenSharePending) {
			if (!enabled) {
				this.queuePendingStopRequest(options);
			}
			logger.debug('Already pending, ignoring request');
			return;
		}
		const existingScreenSharePublications = getLocalScreenSharePublications(participant);
		if (enabled && restartIfEnabled && existingScreenSharePublications.length > 0) {
			await this.setScreenShareEnabled(room, false, {
				sendUpdate: false,
				playSound: false,
			});
			await this.setScreenShareEnabled(
				room,
				true,
				{
					...restOptions,
					sendUpdate,
					playSound,
				},
				publishOptions,
			);
			return;
		}
		if (enabled) {
			await enforceLocalMediaPublicationCap(participant, 'screen_share');
		}
		if (enabled && isDesktop() && isNativeMacOS()) {
			const denied = MediaPermission.isScreenRecordingExplicitlyDenied();
			if (denied) {
				logger.warn('Screen recording permission explicitly denied');
				throw new ScreenRecordingPermissionDeniedError();
			}
			const nativeResult = await ensureNativePermission('screen');
			if (nativeResult === 'denied') {
				logger.warn('Screen recording permission denied');
				MediaPermission.markScreenRecordingExplicitlyDenied();
				throw new ScreenRecordingPermissionDeniedError();
			}
			if (nativeResult === 'granted') {
				MediaPermission.updateScreenRecordingPermissionGranted();
			}
		}
		const stateReason = sendUpdate ? 'user' : 'server';
		const applyState = (value: boolean) => {
			VoiceMediaStateCoordinator.applyScreenShareState(value, {reason: stateReason, sendUpdate});
		};
		if (!enabled) applyState(false);
		if (!enabled) {
			this.setStreamingPriority(false);
			this.cleanupActiveScreenShareEndListener();
			this.cancelEncoderVerification();
			AdaptiveScreenShareEngine.stop();
			SoftwareEncoderWarning.reset();
		}
		if (enabled) {
			SoftwareEncoderWarning.reset();
		}
		const stopCleanupSnapshot = enabled ? null : this.getScreenShareCaptureCleanupSnapshot(participant);
		this.transitionScreenShareLifecycle(
			enabled
				? {type: 'share.start', sourceType: 'display'}
				: {
						type: 'share.stop',
						request: {
							sendUpdate,
							playSound,
						},
					},
		);
		if (enabled) {
			this.setStreamingPriority(true);
		}
		try {
			const effectivePublishOptions = await this.getEffectivePublishOptions(enabled, publishOptions);
			await participant.setScreenShareEnabled(enabled, restOptions, effectivePublishOptions);
			if (enabled) {
				await enforceLocalMediaPublicationCap(participant, 'screen_share');
				applyState(true);
				this.applyScreenShareContentHint(participant);
				await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
				this.ensureScreenShareKeepAliveSink(participant);
				this.applyScreenShareAudioContentHint(participant);
				this.monitorActiveScreenShareEnd(room, participant);
				this.startEncoderVerification(room, participant, effectivePublishOptions?.videoCodec);
				AdaptiveScreenShareEngine.start(room);
			} else {
				await this.cleanupLingeringScreenShareTracks(participant, stopCleanupSnapshot ?? undefined);
				markScreenShareCaptureEnded('screen-share-disabled');
			}
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(enabled);
			this.syncLocalScreenShareAudioState(participant, enabled);
			if (enabled) {
				this.syncPersistedScreenShareAudioPreference(participant);
			}
			if (playSound) {
				if (enabled) {
					SoundCommands.playSound(SoundType.ScreenShareStart);
				} else {
					SoundCommands.playSound(SoundType.ScreenShareStop);
				}
			}
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: enabled,
				sourceType: enabled ? 'display' : null,
				encoderVerificationScheduled: this.encoderVerificationTimer != null,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			logger.info('Success', {enabled});
		} catch (e) {
			if (e instanceof Error && (e.name === 'AbortError' || e.name === 'NotAllowedError')) {
				logger.debug('User cancelled or permission denied', {name: e.name});
				const actual = participant.isScreenShareEnabled;
				if (enabled && !actual) {
					this.setStreamingPriority(false);
					this.clearScreenShareKeepAliveSink();
				}
				if (!actual) {
					await this.cleanupLingeringScreenShareTracks(participant, stopCleanupSnapshot ?? undefined);
					markScreenShareCaptureEnded('screen-share-cancelled');
				}
				applyState(actual);
				updateLocalParticipantFromRoom(room);
				this.syncLocalStreamWatchState(actual);
				this.syncLocalScreenShareAudioState(participant, actual);
				if (actual) {
					this.monitorActiveScreenShareEnd(room, participant);
					AdaptiveScreenShareEngine.start(room);
				}
				this.transitionScreenShareLifecycle({
					type: 'share.cancel',
					active: actual,
					sourceType: actual ? this.getActiveScreenShareSourceType() : null,
				});
			} else {
				logger.error('Failed', {enabled, error: e});
				const actual = participant.isScreenShareEnabled;
				if (enabled && !actual) {
					this.setStreamingPriority(false);
					this.clearScreenShareKeepAliveSink();
				}
				if (!actual) {
					await this.cleanupLingeringScreenShareTracks(participant, stopCleanupSnapshot ?? undefined);
					markScreenShareCaptureEnded('screen-share-failed');
				}
				applyState(actual);
				updateLocalParticipantFromRoom(room);
				this.syncLocalStreamWatchState(actual);
				this.syncLocalScreenShareAudioState(participant, actual);
				if (actual) {
					this.monitorActiveScreenShareEnd(room, participant);
					AdaptiveScreenShareEngine.start(room);
				}
				if (playSound) {
					if (actual) {
						SoundCommands.playSound(SoundType.ScreenShareStart);
					} else {
						SoundCommands.playSound(SoundType.ScreenShareStop);
					}
				}
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: actual,
					sourceType: actual ? this.getActiveScreenShareSourceType() : null,
				});
				throw e;
			}
		}
		if (enabled) {
			await this.applyPendingScreenShareRequests(room, participant);
		}
	}

	async startDeviceScreenShare(
		room: Room | null,
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return;
		}
		const {sendUpdate = true, playSound = true, videoDeviceId, audioDeviceId, resolution} = options || {};
		const participant = room?.localParticipant;
		if (!participant) {
			logger.warn('No participant');
			return;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring device share request');
			return;
		}
		if (getLocalScreenSharePublications(participant).length > 0) {
			await this.setScreenShareEnabled(room, false, {
				sendUpdate: false,
				playSound: false,
			});
		}
		const stateReason = sendUpdate ? 'user' : 'server';
		const applyState = (value: boolean) => {
			VoiceMediaStateCoordinator.applyScreenShareState(value, {reason: stateReason, sendUpdate});
		};
		this.transitionScreenShareLifecycle({type: 'share.start', sourceType: 'device'});
		const createdTracks: Array<LocalAudioTrack | LocalVideoTrack> = [];
		const publishedTracks: Array<LocalAudioTrack | LocalVideoTrack> = [];
		try {
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, publishOptions);
			await ensureNativeCameraPermissionForDeviceShare('start');
			if (audioDeviceId !== undefined) {
				await ensureNativeMicrophonePermissionForDeviceShare('start');
			}
			const videoTrack = await createLocalVideoTrack({
				deviceId: videoDeviceId && videoDeviceId !== 'default' ? videoDeviceId : undefined,
				resolution: resolution
					? {
							width: resolution.width,
							height: resolution.height,
							frameRate: resolution.frameRate,
						}
					: undefined,
			});
			createdTracks.push(videoTrack);
			await applyCameraMirrorProcessor(videoTrack);
			let audioTrack: LocalAudioTrack | undefined;
			if (audioDeviceId !== undefined) {
				audioTrack = await createLocalAudioTrack({
					deviceId: audioDeviceId || undefined,
					echoCancellation: false,
					noiseSuppression: false,
					autoGainControl: false,
					voiceIsolation: false,
					channelCount: 2,
					sampleRate: 48000,
				});
				createdTracks.push(audioTrack);
			}
			await participant.publishTrack(videoTrack, {
				...effectivePublishOptions,
				source: Track.Source.ScreenShare,
				stream: 'screen_share',
			});
			publishedTracks.push(videoTrack);
			if (audioTrack) {
				prepareHighFidelityScreenShareAudioTrack(audioTrack.mediaStreamTrack);
				await participant.publishTrack(audioTrack, SCREEN_SHARE_AUDIO_PUBLISH_OPTIONS);
				publishedTracks.push(audioTrack);
			}
			await enforceLocalMediaPublicationCap(participant, 'screen_share');
			markScreenShareCaptureActive({
				method: 'device-media',
				device: {
					videoDeviceId,
					audioDeviceId,
				},
			});
			this.setStreamingPriority(true);
			applyState(true);
			this.applyScreenShareContentHint(participant, 'device');
			await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
			this.ensureScreenShareKeepAliveSink(participant);
			this.applyScreenShareAudioContentHint(participant);
			this.monitorActiveScreenShareEnd(room, participant);
			this.startEncoderVerification(room, participant, effectivePublishOptions?.videoCodec);
			AdaptiveScreenShareEngine.start(room);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(true);
			this.syncLocalScreenShareAudioState(participant, true);
			if (playSound) {
				SoundCommands.playSound(SoundType.ScreenShareStart);
			}
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: true,
				sourceType: 'device',
				encoderVerificationScheduled: this.encoderVerificationTimer != null,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			logger.info('Started device screen share', {
				videoDeviceId,
				audioIncluded: audioTrack != null,
			});
		} catch (error) {
			if (publishedTracks.length > 0) {
				await Promise.allSettled(publishedTracks.map((track) => participant.unpublishTrack(track)));
			}
			createdTracks.forEach((track) => {
				track.stop();
			});
			const actual = participant.isScreenShareEnabled;
			applyState(actual);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);
			this.syncLocalScreenShareAudioState(participant, actual);
			if (!actual) {
				markScreenShareCaptureEnded('device-screen-share-failed');
			}
			if (actual) {
				AdaptiveScreenShareEngine.start(room);
			}
			this.transitionScreenShareLifecycle({
				type:
					error instanceof Error &&
					(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
						? 'share.cancel'
						: 'share.reject',
				active: actual,
				sourceType: actual ? this.getActiveScreenShareSourceType() : null,
			});
			if (
				error instanceof Error &&
				error.name !== 'AbortError' &&
				error.name !== 'NotAllowedError' &&
				error.name !== 'PermissionDeniedError'
			) {
				logger.error('Failed to start device screen share', {
					error,
					videoDeviceId,
					audioIncluded: audioDeviceId != null,
				});
			}
		}
		await this.applyPendingScreenShareRequests(room, participant);
	}

	async replaceActiveDisplayScreenShare(
		room: Room | null,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share source switching is not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			logger.warn('No active screen share to replace');
			return false;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring screen share source switch');
			return false;
		}
		let didReplace = false;
		this.transitionScreenShareLifecycle({
			type: 'share.replace',
			sourceType: 'display',
			codecRepublishInFlight: true,
		});
		try {
			const tracks = await createDisplayScreenShareTracks(options);
			didReplace = await this.replaceActiveScreenShareTracks(room, participant, tracks, options, publishOptions);
			if (didReplace) {
				this.transitionScreenShareLifecycle({
					type: 'share.resolve',
					active: true,
					sourceType: 'display',
					encoderVerificationScheduled: this.encoderVerificationTimer != null,
					streamingPriorityHeld: this.streamingPriorityHeld,
				});
			} else {
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			}
		} catch (error) {
			if (
				error instanceof Error &&
				(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
			) {
				logger.debug('User cancelled or denied screen share source switch', {name: error.name});
				this.transitionScreenShareLifecycle({
					type: 'share.cancel',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			} else {
				logger.error('Failed to replace active display screen share source', {error});
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			}
		}
		await this.applyPendingScreenShareRequests(room, participant);
		return didReplace;
	}

	async replaceActiveDeviceScreenShare(
		room: Room | null,
		options?: DeviceScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share source switching is not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			logger.warn('No active screen share to replace');
			return false;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring device share source switch');
			return false;
		}
		let didReplace = false;
		this.transitionScreenShareLifecycle({type: 'share.replace', sourceType: 'device'});
		try {
			await ensureNativeCameraPermissionForDeviceShare('replace');
			if (options?.audioDeviceId !== undefined) {
				await ensureNativeMicrophonePermissionForDeviceShare('replace');
			}
			const tracks = await createDeviceReplacementTracks(options);
			didReplace = await this.replaceActiveScreenShareTracks(
				room,
				participant,
				tracks,
				undefined,
				publishOptions,
				'device',
			);
			if (didReplace) {
				this.transitionScreenShareLifecycle({
					type: 'share.resolve',
					active: true,
					sourceType: 'device',
					encoderVerificationScheduled: this.encoderVerificationTimer != null,
					streamingPriorityHeld: this.streamingPriorityHeld,
				});
			} else {
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			}
		} catch (error) {
			if (
				error instanceof Error &&
				(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
			) {
				logger.debug('User cancelled or denied device share source switch', {name: error.name});
				this.transitionScreenShareLifecycle({
					type: 'share.cancel',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			} else {
				logger.error('Failed to replace active device screen share source', {
					error,
					videoDeviceId: options?.videoDeviceId,
					audioIncluded: options?.audioDeviceId != null,
				});
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			}
		}
		await this.applyPendingScreenShareRequests(room, participant);
		return didReplace;
	}

	private resolveNativeEngineScreenShareOptionsFromActiveSource(
		options?: ScreenShareCaptureOptions,
	): NativeScreenShareOptions | null {
		const sourceId = ActiveScreenShareSource.getSourceId();
		if (!sourceId) return null;
		const kind: NativeScreenCaptureSource['kind'] = sourceId.startsWith('window:') ? 'window' : 'screen';
		const resolution = options?.resolution ?? undefined;
		const source: NativeScreenCaptureSource = {
			kind,
			id: sourceId,
			name: kind === 'window' ? 'Window' : 'Screen',
			width: resolution?.width ?? 1920,
			height: resolution?.height ?? 1080,
		};
		return {
			source,
			resolution,
			contentHint: options?.contentHint,
		};
	}

	private async startNativeEngineScreenShare(
		nativeOptions: NativeScreenShareOptions,
		options?: {sendUpdate?: boolean; playSound?: boolean},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		const {sendUpdate = true, playSound = true} = options || {};
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring native-engine screen share request');
			return;
		}
		const stateReason = sendUpdate ? 'user' : 'server';
		const sourceType = this.getNativeScreenShareSourceType(nativeOptions);
		this.transitionScreenShareLifecycle({type: 'share.start', sourceType});
		try {
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, publishOptions);
			const capture = await startNativeCaptureForEngine(nativeOptions);
			this.nativeEngineScreenShareCaptureId = capture.captureId;
			await getVoiceEngine().publishScreenShare({
				captureId: capture.captureId,
				width: capture.width,
				height: capture.height,
				codec: effectivePublishOptions?.videoCodec,
				maxBitrateBps: effectivePublishOptions?.screenShareEncoding?.maxBitrate,
				maxFramerate: effectivePublishOptions?.screenShareEncoding?.maxFramerate,
			});
			this.setStreamingPriority(true);
			VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: stateReason, sendUpdate});
			this.syncLocalStreamWatchState(true);
			if (playSound) {
				SoundCommands.playSound(SoundType.ScreenShareStart);
			}
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: true,
				sourceType,
				encoderVerificationScheduled: false,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			logger.info('Started native-engine screen share', {
				sourceKind: nativeOptions.source.kind,
				captureId: capture.captureId,
			});
		} catch (error) {
			logger.warn('Failed to start native-engine screen share', {error});
			await this.stopNativeEngineScreenShare({sendUpdate, playSound: false}).catch(() => undefined);
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: false,
				sourceType,
				encoderVerificationScheduled: false,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			throw error;
		}
	}

	private async stopNativeEngineScreenShare(options?: {sendUpdate?: boolean; playSound?: boolean}): Promise<void> {
		const {sendUpdate = true, playSound = true} = options || {};
		const captureId = this.nativeEngineScreenShareCaptureId;
		this.nativeEngineScreenShareCaptureId = null;
		this.clearScreenShareKeepAliveSink();
		this.setStreamingPriority(false);
		try {
			await getVoiceEngine().unpublishScreenShare();
		} catch (error) {
			logger.warn('Native-engine screen-share unpublish failed', {error});
		}
		if (captureId) {
			await stopNativeCaptureForEngine(captureId);
		}
		markScreenShareCaptureEnded('native-engine-screen-share-stopped');
		VoiceMediaStateCoordinator.applyScreenShareState(false, {
			reason: sendUpdate ? 'user' : 'server',
			sendUpdate,
		});
		this.syncLocalStreamWatchState(false);
		if (playSound) {
			SoundCommands.playSound(SoundType.ScreenShareStop);
		}
	}

	async startNativeDisplayScreenShare(
		room: Room | null,
		nativeOptions: NativeScreenShareOptions,
		options?: {
			sendUpdate?: boolean;
			playSound?: boolean;
		},
		publishOptions?: TrackPublishOptions,
	): Promise<void> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share not supported on native');
			return;
		}
		if (await shouldUseNativeVoiceEngine()) {
			await this.startNativeEngineScreenShare(nativeOptions, options, publishOptions);
			return;
		}
		const {sendUpdate = true, playSound = true} = options || {};
		const participant = room?.localParticipant;
		if (!participant) {
			logger.warn('No participant');
			return;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring native screen share request');
			return;
		}
		if (getLocalScreenSharePublications(participant).length > 0) {
			await this.setScreenShareEnabled(room, false, {
				sendUpdate: false,
				playSound: false,
			});
		}
		const stateReason = sendUpdate ? 'user' : 'server';
		const applyState = (value: boolean) => {
			VoiceMediaStateCoordinator.applyScreenShareState(value, {reason: stateReason, sendUpdate});
		};
		const sourceType = this.getNativeScreenShareSourceType(nativeOptions);
		this.transitionScreenShareLifecycle({type: 'share.start', sourceType});
		let capturedTracks: CapturedScreenShareTracks | null = null;
		let videoPublished = false;
		try {
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, publishOptions);
			capturedTracks = await createNativeScreenShareTracks(nativeOptions);
			await participant.publishTrack(capturedTracks.videoTrack, {
				...effectivePublishOptions,
				source: Track.Source.ScreenShare,
				stream: 'screen_share',
			});
			videoPublished = true;
			if (capturedTracks.audioTrack) {
				try {
					prepareHighFidelityScreenShareAudioTrack(capturedTracks.audioTrack);
					await participant.publishTrack(capturedTracks.audioTrack, SCREEN_SHARE_AUDIO_PUBLISH_OPTIONS);
					await this.unmuteScreenShareAudioPublication(participant, 'publish native screen-share audio');
					commitNativeAudioBridgeReplacement();
				} catch (error) {
					logger.warn('Failed to publish native screen-share audio; degrading to video-only', {error});
					try {
						capturedTracks.audioTrack.stop();
					} catch {}
				}
			}
			await enforceLocalMediaPublicationCap(participant, 'screen_share');
			this.setStreamingPriority(true);
			applyState(true);
			this.applyScreenShareContentHint(participant, nativeOptions.source.kind === 'window' ? 'app' : 'display');
			await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
			this.ensureScreenShareKeepAliveSink(participant);
			this.applyScreenShareAudioContentHint(participant);
			this.monitorActiveScreenShareEnd(room, participant);
			this.startEncoderVerification(room, participant, effectivePublishOptions?.videoCodec);
			AdaptiveScreenShareEngine.start(room);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(true);
			this.syncLocalScreenShareAudioState(participant, true);
			this.syncPersistedScreenShareAudioPreference(participant);
			if (playSound) {
				SoundCommands.playSound(SoundType.ScreenShareStart);
			}
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: true,
				sourceType,
				encoderVerificationScheduled: this.encoderVerificationTimer != null,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			logger.info('Started native screen share', {
				sourceKind: nativeOptions.source.kind,
				audioIncluded: capturedTracks.audioTrack != null,
			});
		} catch (error) {
			if (!videoPublished && capturedTracks) {
				stopMediaTrack(capturedTracks.videoTrack);
				stopMediaTrack(capturedTracks.audioTrack);
			}
			const actual = participant.isScreenShareEnabled;
			applyState(actual);
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);
			this.syncLocalScreenShareAudioState(participant, actual);
			if (!actual) {
				markScreenShareCaptureEnded('native-screen-share-failed');
			}
			if (actual) {
				AdaptiveScreenShareEngine.start(room);
			}
			this.transitionScreenShareLifecycle({
				type:
					error instanceof Error &&
					(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
						? 'share.cancel'
						: 'share.reject',
				active: actual,
				sourceType: actual ? sourceType : null,
			});
			if (
				error instanceof Error &&
				error.name !== 'AbortError' &&
				error.name !== 'NotAllowedError' &&
				error.name !== 'PermissionDeniedError'
			) {
				logger.error('Failed to start native screen share', {
					error,
					sourceKind: nativeOptions.source.kind,
				});
			}
		}
		await this.applyPendingScreenShareRequests(room, participant);
	}

	async replaceActiveNativeDisplayScreenShare(
		room: Room | null,
		nativeOptions: NativeScreenShareOptions,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share source switching is not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			logger.warn('No active screen share to replace');
			return false;
		}
		if (this.isScreenSharePending) {
			logger.debug('Already pending, ignoring native screen share source switch');
			return false;
		}
		let didReplace = false;
		const sourceType = this.getNativeScreenShareSourceType(nativeOptions);
		this.transitionScreenShareLifecycle({type: 'share.replace', sourceType});
		try {
			const tracks = await createNativeScreenShareTracks(nativeOptions);
			didReplace = await this.replaceActiveScreenShareTracks(
				room,
				participant,
				tracks,
				options,
				publishOptions,
				nativeOptions.source.kind === 'window' ? 'app' : 'display',
			);
			if (didReplace) {
				this.transitionScreenShareLifecycle({
					type: 'share.resolve',
					active: true,
					sourceType,
					encoderVerificationScheduled: this.encoderVerificationTimer != null,
					streamingPriorityHeld: this.streamingPriorityHeld,
				});
			} else {
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			}
		} catch (error) {
			if (
				error instanceof Error &&
				(error.name === 'AbortError' || error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError')
			) {
				logger.debug('User cancelled or denied native screen share source switch', {name: error.name});
				this.transitionScreenShareLifecycle({
					type: 'share.cancel',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			} else {
				logger.error('Failed to replace active native display screen share source', {
					error,
					sourceKind: nativeOptions.source.kind,
				});
				this.transitionScreenShareLifecycle({
					type: 'share.reject',
					active: participant.isScreenShareEnabled,
					sourceType: participant.isScreenShareEnabled ? this.getActiveScreenShareSourceType() : null,
				});
			}
		}
		await this.applyPendingScreenShareRequests(room, participant);
		return didReplace;
	}

	async updateActiveScreenShareSettings(
		room: Room | null,
		options?: ScreenShareCaptureOptions,
		publishOptions?: TrackPublishOptions,
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			logger.warn('Screen share updates are not supported on native');
			return false;
		}
		const participant = room?.localParticipant;
		if (!participant || !participant.isScreenShareEnabled) {
			return false;
		}
		const screenSharePublication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = screenSharePublication?.videoTrack;
		if (!screenShareTrack) {
			logger.warn('No active screen share track to update');
			return false;
		}
		let appliedAnySetting = false;
		if (typeof options?.audio === 'boolean') {
			const screenShareAudioPublication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
			if (screenShareAudioPublication) {
				try {
					if (options.audio) {
						await screenShareAudioPublication.unmute();
					} else {
						await screenShareAudioPublication.mute();
					}
					appliedAnySetting = true;
				} catch (error) {
					logger.warn('Failed to update active screen share audio state', {
						error,
						includeAudio: options.audio,
					});
				}
			} else if (options.audio) {
				logger.info('Cannot enable screen share audio without restarting screen share');
			}
		}
		const resolution = options?.resolution;
		if (resolution) {
			const nextConstraints: MediaTrackConstraints = {};
			if (resolution.width > 0) {
				nextConstraints.width = {ideal: resolution.width};
			}
			if (resolution.height > 0) {
				nextConstraints.height = {ideal: resolution.height};
			}
			if (resolution.frameRate !== undefined) {
				nextConstraints.frameRate = resolution.frameRate;
			}
			try {
				await screenShareTrack.mediaStreamTrack.applyConstraints(nextConstraints);
				appliedAnySetting = true;
			} catch (error) {
				logger.warn('Failed to update active screen share constraints', {
					error,
					resolution,
				});
			}
		}
		if (options && Object.hasOwn(options, 'contentHint')) {
			screenShareTrack.mediaStreamTrack.contentHint = options.contentHint ?? '';
			appliedAnySetting = true;
		}
		await screenShareTrack.setDegradationPreference(SCREEN_SHARE_DEGRADATION_PREFERENCE);
		appliedAnySetting = true;
		await this.enforceScreenShareSenderParameters(participant, publishOptions);
		this.ensureScreenShareKeepAliveSink(participant);
		appliedAnySetting = true;
		updateLocalParticipantFromRoom(room);
		this.syncLocalScreenShareAudioState(participant, participant.isScreenShareEnabled);
		return appliedAnySetting;
	}

	private getScreenShareCodecMigrationPublishOptions(
		publishOptions: TrackPublishOptions | undefined,
		trackName: string | undefined,
	): TrackPublishOptions {
		return {
			...publishOptions,
			source: Track.Source.ScreenShare,
			stream: 'screen_share',
			...(trackName ? {name: trackName} : {}),
		};
	}

	private getScreenShareCodecMigrationPublicationTrack(
		publication: LocalTrackPublication,
		role: 'candidate' | 'fallback',
	): {track: LocalVideoTrack; trackSid: string} {
		const track = publication.videoTrack as LocalVideoTrack | undefined;
		if (!track) {
			throw new Error(`screen share codec migration ${role} published without a local video track`);
		}
		const trackSid = publication.trackSid;
		if (!trackSid) {
			throw new Error(`screen share codec migration ${role} published without a track SID`);
		}
		return {track, trackSid};
	}

	private async activateScreenShareCodecMigrationPublication(args: {
		room: Room;
		participant: LocalParticipant;
		publication: LocalTrackPublication;
		track: LocalVideoTrack;
		publishOptions?: TrackPublishOptions;
		reapplySenderParameters?: boolean;
	}): Promise<void> {
		const {room, participant, publication, track, publishOptions, reapplySenderParameters = false} = args;
		await enforceLocalMediaPublicationCap(participant, 'screen_share', {preferredPublication: publication});
		this.applyScreenShareContentHint(participant, undefined, track);
		if (reapplySenderParameters) {
			await this.enforceScreenShareTrackSenderParameters(track, publishOptions);
		}
		this.ensureScreenShareKeepAliveSink(participant, track);
		this.monitorActiveScreenShareEnd(room, participant, track);
		this.startEncoderVerification(room, participant, publishOptions?.videoCodec, track);
		AdaptiveScreenShareEngine.start(room);
		VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'server', sendUpdate: false});
		updateLocalParticipantFromRoom(room);
		this.syncLocalStreamWatchState(true);
		this.syncLocalScreenShareAudioState(participant, true);
	}

	private async migrateActiveScreenShareCodecBreakBeforeMake(args: {
		room: Room;
		participant: LocalParticipant;
		publication: LocalTrackPublication;
		screenShareTrack: LocalVideoTrack;
		mediaStreamTrack: MediaStreamTrack;
		previousOptions: TrackPublishOptions;
		nextPublishOptions: TrackPublishOptions;
		currentCodec?: VideoCodec;
		codec: VideoCodec;
		reason: NegotiationReason;
		forced: boolean;
	}): Promise<boolean> {
		const {
			room,
			participant,
			publication,
			screenShareTrack,
			mediaStreamTrack,
			previousOptions,
			nextPublishOptions,
			currentCodec,
			codec,
			reason,
			forced,
		} = args;
		const migrationId = ScreenSharePublicationMigration.createMigrationId();
		const generation = ++this.screenShareMigrationGeneration;
		const previousTrackSid = publication.trackSid ?? null;
		const previousTrackName = publication.trackName;
		let candidateMediaStreamTrack: MediaStreamTrack | null = null;
		let candidatePublication: LocalTrackPublication | null = null;
		let candidateTrack: LocalVideoTrack | undefined;
		let oldVideoUnpublished = false;
		let committedTrackSid: string | null = null;
		this.transitionScreenShareLifecycle({type: 'share.codecRepublish'});
		const publishBreakMessage = {
			migration_id: migrationId,
			generation,
			previous_track_sid: previousTrackSid,
			codec,
			reason,
		};
		const commitMigration = async (trackSid: string): Promise<void> => {
			const commitMessage = {
				migration_id: migrationId,
				generation,
				previous_track_sid: previousTrackSid,
				candidate_track_sid: trackSid,
			};
			await ScreenSharePublicationMigration.publishCommit(room, commitMessage).catch((error) => {
				logger.warn('Failed to publish screen share codec migration commit', {
					error,
					migrationId,
					trackSid,
				});
			});
			ScreenSharePublicationMigration.markLocalMigrationCommitted(participant.identity, commitMessage);
			committedTrackSid = trackSid;
		};
		const abortMigration = async (abortReason: string): Promise<void> => {
			if (committedTrackSid) return;
			const abortMessage = {
				migration_id: migrationId,
				generation,
				candidate_track_sid: candidatePublication?.trackSid ?? null,
				reason: abortReason,
			};
			await ScreenSharePublicationMigration.publishAbort(room, abortMessage).catch(() => {});
			ScreenSharePublicationMigration.markLocalMigrationAborted(participant.identity, abortMessage);
		};
		const restorePreviousCodec = async (restoreError: unknown): Promise<boolean> => {
			if (!oldVideoUnpublished || mediaStreamTrack.readyState === 'ended') {
				return false;
			}
			try {
				const effectivePreviousOptions = await this.getEffectivePublishOptions(true, previousOptions);
				const fallbackPublication = await participant.publishTrack(
					mediaStreamTrack,
					this.getScreenShareCodecMigrationPublishOptions(effectivePreviousOptions, previousTrackName),
				);
				const fallbackTrackInfo = this.getScreenShareCodecMigrationPublicationTrack(fallbackPublication, 'fallback');
				await this.enforceScreenShareTrackSenderParameters(fallbackTrackInfo.track, effectivePreviousOptions);
				await commitMigration(fallbackTrackInfo.trackSid);
				await this.activateScreenShareCodecMigrationPublication({
					room,
					participant,
					publication: fallbackPublication,
					track: fallbackTrackInfo.track,
					publishOptions: effectivePreviousOptions,
				});
				logger.warn('Restored previous screen share codec after break-before-make migration failed', {
					error: restoreError,
					migrationId,
					previousCodec: currentCodec,
					failedCodec: codec,
					reason,
				});
				return true;
			} catch (fallbackError) {
				logger.warn('Failed to restore previous screen share codec after break-before-make migration failed', {
					error: fallbackError,
					originalError: restoreError,
					migrationId,
					previousCodec: currentCodec,
					failedCodec: codec,
				});
				return false;
			}
		};
		try {
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, nextPublishOptions);
			candidateMediaStreamTrack =
				typeof mediaStreamTrack.clone === 'function' ? mediaStreamTrack.clone() : mediaStreamTrack;
			this.applyScreenShareContentHintToMediaTrack(candidateMediaStreamTrack);
			this.cancelEncoderVerification();
			AdaptiveScreenShareEngine.stop();
			ScreenSharePublicationMigration.markLocalMigrationBreaking(participant.identity, publishBreakMessage);
			await ScreenSharePublicationMigration.publishBreak(room, {
				migrationId,
				generation,
				previousTrackSid,
				codec,
				reason,
			}).catch((error) => {
				logger.warn('Failed to publish screen share codec break-before-make announcement', {
					error,
					migrationId,
					codec,
					reason,
				});
			});
			this.cleanupActiveScreenShareEndListener();
			VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'server', sendUpdate: false});
			const codecRepublishCleanupSnapshot = this.getScreenShareSimulcastCleanupSnapshot(screenShareTrack);
			await participant.unpublishTrack(screenShareTrack, false);
			oldVideoUnpublished = true;
			VoiceMediaStateCoordinator.applyScreenShareState(true, {reason: 'server', sendUpdate: false});
			await releaseScreenShareCaptureCleanup(codecRepublishCleanupSnapshot);
			candidatePublication = await participant.publishTrack(
				candidateMediaStreamTrack,
				this.getScreenShareCodecMigrationPublishOptions(effectivePublishOptions, previousTrackName),
			);
			const candidateTrackInfo = this.getScreenShareCodecMigrationPublicationTrack(candidatePublication, 'candidate');
			candidateTrack = candidateTrackInfo.track;
			await this.enforceScreenShareTrackSenderParameters(candidateTrack, effectivePublishOptions);
			await commitMigration(candidateTrackInfo.trackSid);
			if (candidateMediaStreamTrack !== mediaStreamTrack) {
				stopMediaTrack(mediaStreamTrack);
			}
			await this.activateScreenShareCodecMigrationPublication({
				room,
				participant,
				publication: candidatePublication,
				track: candidateTrack,
				publishOptions: effectivePublishOptions,
				reapplySenderParameters: true,
			});
			this.transitionScreenShareLifecycle({
				type: 'share.resolve',
				active: true,
				sourceType: this.getActiveScreenShareSourceType(),
				encoderVerificationScheduled: this.encoderVerificationTimer != null,
				streamingPriorityHeld: this.streamingPriorityHeld,
			});
			logger.info('Migrated active screen share codec with break-before-make publication switch', {
				migrationId,
				previousCodec: currentCodec,
				codec,
				reason,
				forced,
				strategy: 'break-before-make',
			});
			return true;
		} catch (error) {
			logger.warn('Failed to migrate active screen share codec with break-before-make', {
				error,
				migrationId,
				codec,
				reason,
			});
			if (!committedTrackSid && candidateTrack) {
				await participant.unpublishTrack(candidateTrack, false).catch((unpublishError) => {
					logger.warn('Failed to unpublish screen share codec migration candidate after abort', {
						error: unpublishError,
						migrationId,
					});
				});
			}
			if (!committedTrackSid && candidateMediaStreamTrack && candidateMediaStreamTrack !== mediaStreamTrack) {
				stopMediaTrack(candidateMediaStreamTrack);
			}
			if (!committedTrackSid && (await restorePreviousCodec(error))) {
				this.transitionScreenShareLifecycle({
					type: 'share.resolve',
					active: true,
					sourceType: this.getActiveScreenShareSourceType(),
					encoderVerificationScheduled: this.encoderVerificationTimer != null,
					streamingPriorityHeld: this.streamingPriorityHeld,
				});
				logger.info('Completed screen share codec migration by falling back to previous codec', {
					migrationId,
					previousCodec: currentCodec,
					failedCodec: codec,
					reason,
					forced,
				});
				return true;
			}
			await abortMigration(oldVideoUnpublished ? 'publisher-error' : 'unpublish-error');
			const actual = participant.isScreenShareEnabled;
			VoiceMediaStateCoordinator.applyScreenShareState(actual, {reason: 'user', sendUpdate: !actual});
			updateLocalParticipantFromRoom(room);
			this.syncLocalStreamWatchState(actual);
			this.syncLocalScreenShareAudioState(participant, actual);
			if (actual) {
				this.monitorActiveScreenShareEnd(room, participant);
				AdaptiveScreenShareEngine.start(room);
				await this.enforceScreenShareSenderParameters(participant, previousOptions);
				this.ensureScreenShareKeepAliveSink(participant);
			} else {
				await this.cleanupLingeringScreenShareTracks(participant);
				markScreenShareCaptureEnded('screen-share-codec-migration-failed');
			}
			this.transitionScreenShareLifecycle({
				type: 'share.reject',
				active: actual,
				sourceType: actual ? this.getActiveScreenShareSourceType() : null,
			});
			return false;
		} finally {
			await this.applyPendingScreenShareRequests(room, participant);
		}
	}

	async renegotiateActiveScreenShareCodec(
		room: Room | null,
		codec: VideoCodec,
		reason: NegotiationReason,
		options: {force?: boolean} = {},
	): Promise<boolean> {
		if (Platform.OS !== 'web') {
			return false;
		}
		const participant = room?.localParticipant;
		if (!room || !participant?.isScreenShareEnabled) {
			return false;
		}
		if (this.isScreenSharePending) {
			this.queuePendingCodecRepublishRequest(codec, reason, options);
			return false;
		}
		await enforceLocalMediaPublicationCap(participant, 'screen_share');
		const publication = participant.getTrackPublication(Track.Source.ScreenShare);
		const screenShareTrack = publication?.videoTrack as LocalVideoTrack | undefined;
		if (!publication || !screenShareTrack || screenShareTrack.mediaStreamTrack.readyState === 'ended') {
			return false;
		}
		const mediaStreamTrack = screenShareTrack.mediaStreamTrack;
		const previousOptions = ((publication as {options?: TrackPublishOptions}).options ?? {}) as TrackPublishOptions;
		const currentCodec = screenShareTrack.codec ?? previousOptions.videoCodec;
		const decision = selectScreenShareCodecRepublishDecision({
			currentCodec,
			nextCodec: codec,
			reason,
			force: options.force,
		});
		if (decision.action === 'noop') {
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, {
				...previousOptions,
				...(currentCodec ? {videoCodec: currentCodec} : {}),
			});
			await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
			return false;
		}
		if (decision.action === 'defer') {
			this.deferActiveCodecRepublishRequest(codec, reason, options);
			logger.info('Deferring negotiated screen share codec change until the next share start', {
				currentCodec,
				codec,
				reason,
			});
			return false;
		}
		const codecChanged = currentCodec !== codec;
		const nextPublishOptions = {
			...previousOptions,
			videoCodec: codec,
		};
		if (options.force || codecChanged) {
			delete nextPublishOptions.backupCodec;
			delete nextPublishOptions.backupCodecPolicy;
			delete nextPublishOptions.scalabilityMode;
			delete nextPublishOptions.simulcast;
		}
		if (currentCodec === codec && !options.force) {
			const effectivePublishOptions = await this.getEffectivePublishOptions(true, nextPublishOptions);
			await this.enforceScreenShareSenderParameters(participant, effectivePublishOptions);
			return false;
		}
		return this.migrateActiveScreenShareCodecBreakBeforeMake({
			room,
			participant,
			publication,
			screenShareTrack,
			mediaStreamTrack,
			previousOptions,
			nextPublishOptions,
			currentCodec,
			codec,
			reason,
			forced: options.force === true,
		});
	}

	setScreenShareAudioMuted(room: Room | null, muted: boolean): void {
		const participant = room?.localParticipant;
		if (!participant) return;
		const publication = participant.getTrackPublication(Track.Source.ScreenShareAudio);
		if (!publication) return;
		const operation = muted ? publication.mute() : publication.unmute();
		operation.catch((error) => {
			logger.warn('Failed to apply immediate screen share audio mute', {error, muted});
		});
		this.syncLocalScreenShareAudioState(participant, !muted);
	}

	async toggleScreenShareFromKeybind(room: Room | null): Promise<void> {
		const current = LocalVoiceState.getSelfStream();
		await this.setScreenShareEnabled(room, !current);
	}

	resetStreamTracking(): void {
		this.clearScreenShareKeepAliveSink();
		this.setStreamingPriority(false);
		this.transitionScreenShareLifecycle({type: 'share.reset'});
		this.cleanupActiveScreenShareEndListener();
		this.cancelEncoderVerification();
		AdaptiveScreenShareEngine.stop();
		this.endedScreenShareStopInFlight = null;
		this.cleanupScreenShareAudioRoutingState();
	}
}

export default new VoiceScreenShareManager();
